import { FormEvent, useState } from "react";
import { CheckCircle2, Instagram, Linkedin, Loader2, Mail, MapPin, Send, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { submitPublicContactMessage } from "../../Lib/contactApi";

export default function ContactSection() {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");
    const [fields, setFields] = useState({ name: "", email: "", subject: "", message: "", website: "" });
    const update = (field: keyof typeof fields, value: string) => setFields((current) => ({ ...current, [field]: value }));
    const submit = async (event: FormEvent) => {
        event.preventDefault(); setSubmitting(true); setError("");
        try {
            await submitPublicContactMessage({ ...fields, sourcePage: window.location.pathname || "/" });
            setSubmitted(true);
        } catch (cause: any) {
            setError(cause?.message || "Your message could not be sent. Please try again.");
        } finally { setSubmitting(false); }
    };

    return <section className="mz-section mz-section--soft" id="contact">
        <div className="mz-container mz-contact">
            <div className="mz-contact__intro">
                <p className="mz-section-label">Contact MockZo</p>
                <h2 className="mz-section-title">Have a question? Let's talk.</h2>
                <p className="mz-section-lead">Ask us about workshops, internships, mock interviews or your student journey.</p>
                <div className="mz-contact__details">
                    <a href="mailto:contact@mockzo.com"><Mail /> contact@mockzo.com</a>
                    <span><MapPin /> Hyderabad, India</span>
                </div>
                <div className="mz-contact__socials">
                    <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin /></a>
                    <a href="https://www.instagram.com/" target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram /></a>
                    <a href="https://www.youtube.com/" target="_blank" rel="noreferrer" aria-label="YouTube"><Youtube /></a>
                </div>
                <p className="mz-contact__legal"><Link to="/terms">Terms and Conditions</Link><span>·</span><Link to="/privacy">Privacy Policy</Link></p>
            </div>
            <div className="mz-contact__card">
                {!submitted ? <>
                    <h3>Send us a message</h3>
                    <p>Fill this out and we'll get back to you soon.</p>
                    <form onSubmit={submit}>
                        <input aria-hidden="true" tabIndex={-1} autoComplete="off" name="website" value={fields.website} onChange={(event) => update("website", event.target.value)} className="sr-only" />
                        <div className="mz-form-row">
                            <Field label="Name" value={fields.name} onChange={(value) => update("name", value)} placeholder="Your full name" />
                            <Field label="Email" value={fields.email} onChange={(value) => update("email", value)} placeholder="you@example.com" type="email" />
                        </div>
                        <Field label="Subject" value={fields.subject} onChange={(value) => update("subject", value)} placeholder="How can we help?" />
                        <label>Message<textarea required value={fields.message} onChange={(event) => update("message", event.target.value)} rows={5} maxLength={2000} placeholder="Tell us what you need help with…" /></label>
                        {error && <p className="mz-interest-form__error" role="alert">{error}</p>}
                        <div className="mz-form-submit"><small>We typically respond within 24 hours.</small><button className="mz-button mz-button--dark" disabled={submitting}>{submitting ? <Loader2 className="animate-spin" /> : <Send />} {submitting ? "Sending…" : "Send Message"}</button></div>
                    </form>
                </> : <div className="mz-contact__success"><CheckCircle2 /><h3>Message sent!</h3><p>Thanks for reaching out. We'll get back to you shortly.</p><button className="mz-button mz-button--light" onClick={() => setSubmitted(false)}>Send another message</button></div>}
            </div>
        </div>
    </section>;
}

function Field({ label, value, onChange, placeholder, type = "text" }: { label: string; value: string; onChange: (value: string) => void; placeholder: string; type?: string }) {
    return <label>{label}<input required value={value} onChange={(event) => onChange(event.target.value)} type={type} placeholder={placeholder} /></label>;
}
